import { selectCrossLaneCouplingsForTarget, selectCrossLaneCouplingsFromMetadata } from "../sync/sync.selectors";
import type { LaneItem, LaneLodTier, LaneProductType, LaneViewportContext } from "../lane.types";

export type VisualDensity = "micro" | "compact" | "regular";

export type VisibleLaneItem<TItem extends LaneItem = LaneItem> = {
  item: TItem;
  leftPx: number;
  widthPx: number;
};

export const GLASS_HEADER_META_TEXT_CLASS = "text-slate-700/88 [text-shadow:0_0_8px_rgba(255,255,255,0.18)] dark:text-white/82 dark:[text-shadow:0_0_12px_rgba(255,255,255,0.18)]";
export const GLASS_SECONDARY_TEXT_CLASS = "text-slate-700/92 [text-shadow:0_0_10px_rgba(255,255,255,0.16)] dark:text-current/95 dark:[text-shadow:0_0_16px_rgba(255,255,255,0.18)]";
export const GLASS_WHITE_SECONDARY_TEXT_CLASS = "text-slate-700/90 [text-shadow:0_0_8px_rgba(255,255,255,0.16)] dark:text-white/90 dark:[text-shadow:0_0_14px_rgba(255,255,255,0.18)]";
export const GLASS_SLATE_SECONDARY_TEXT_CLASS = "text-slate-700/90 [text-shadow:0_0_8px_rgba(255,255,255,0.14)] dark:text-slate-50/92 dark:[text-shadow:0_0_14px_rgba(255,255,255,0.16)]";

export function laneMicroLabelTextClass(productType: LaneProductType) {
  switch (productType) {
    case "audio_primary":
    case "audio_analysis":
      return "text-sky-900/88 dark:text-cyan-50/92";
    case "npc_logic":
      return "text-slate-900/88 dark:text-violet-50/92";
    case "projection_emotion":
      return "text-slate-900/88 dark:text-fuchsia-50/92";
    case "projection_haptic":
    case "projection_continuity":
      return "text-slate-900/88 dark:text-amber-50/92";
    case "video_primary":
    default:
      return "text-sky-900/88 dark:text-sky-50/92";
  }
}

export function laneMicroLabelPlateClass(productType: LaneProductType) {
  switch (productType) {
    case "audio_primary":
    case "audio_analysis":
      return "border-cyan-300/24 bg-cyan-100/78 dark:border-cyan-300/16 dark:bg-cyan-300/10";
    case "npc_logic":
      return "border-violet-300/24 bg-violet-100/76 dark:border-violet-300/16 dark:bg-violet-300/10";
    case "projection_emotion":
      return "border-fuchsia-300/24 bg-fuchsia-100/76 dark:border-fuchsia-300/16 dark:bg-fuchsia-300/10";
    case "projection_haptic":
    case "projection_continuity":
      return "border-amber-300/24 bg-amber-100/78 dark:border-amber-300/16 dark:bg-amber-300/10";
    case "video_primary":
    default:
      return "border-sky-300/24 bg-sky-100/78 dark:border-sky-300/16 dark:bg-sky-300/10";
  }
}

export function clipRangeToViewport(startSec: number, endSec: number, viewport: LaneViewportContext) {
  const leftSec = Math.max(startSec, viewport.viewportStartSec);
  const rightSec = Math.min(endSec, viewport.viewportEndSec);
  if (rightSec <= leftSec) return null;

  return {
    leftPx: Math.max(0, (leftSec - viewport.viewportStartSec) * viewport.pxPerSec),
    widthPx: Math.max(6, (rightSec - leftSec) * viewport.pxPerSec),
  };
}

export function clipItemToViewport(item: LaneItem, viewport: LaneViewportContext) {
  return clipRangeToViewport(item.startSec, item.endSec, viewport);
}

export function visibleLaneItems<TItem extends LaneItem>(items: TItem[], viewport: LaneViewportContext): VisibleLaneItem<TItem>[] {
  return items
    .map((item) => {
      const clipped = clipItemToViewport(item, viewport);
      if (!clipped) return null;
      return {
        item,
        leftPx: clipped.leftPx,
        widthPx: clipped.widthPx,
      } satisfies VisibleLaneItem<TItem>;
    })
    .filter((entry): entry is VisibleLaneItem<TItem> => entry != null);
}

export function itemVisualDensity(widthPx: number): VisualDensity {
  if (widthPx < 72) return "micro";
  if (widthPx < 160) return "compact";
  return "regular";
}

export function overlapCounts(visibleItems: VisibleLaneItem[]): Map<string, number> {
  const counts = new Map<string, number>();

  for (const current of visibleItems) {
    let overlapCount = 0;
    for (const candidate of visibleItems) {
      const left = Math.max(current.leftPx, candidate.leftPx);
      const right = Math.min(current.leftPx + current.widthPx, candidate.leftPx + candidate.widthPx);
      if (right - left > 4) overlapCount += 1;
    }
    counts.set(current.item.id, overlapCount);
  }

  return counts;
}

export function readNumberMeta(item: LaneItem, key: string): number | null {
  const value = item.metadata?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function readStringMeta(item: LaneItem, key: string): string | null {
  const value = item.metadata?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function formatSigned(value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${Math.abs(value).toFixed(2)}`;
}

export function continuityStatusTone(status: string, severity: string | undefined) {
  if (severity === "critical" || status === "abrupt" || status === "mismatched" || status === "unresolved") {
    return "border-rose-300/28 bg-rose-400/18 text-rose-50";
  }
  if (severity === "warning" || status === "dense" || status === "gapped") {
    return "border-amber-300/28 bg-amber-400/18 text-amber-50";
  }
  return "border-emerald-300/24 bg-emerald-400/14 text-emerald-50";
}

export function productTone(productType: LaneProductType) {
  switch (productType) {
    case "video_primary":
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(56,189,248,0.08),rgba(2,6,23,0.04))]",
        accentClassName: "from-sky-300/24 via-sky-300/8 to-transparent",
        chipClassName: "border-sky-300/20 bg-sky-300/12 text-sky-50/88",
        emptyClassName: "border-sky-300/20 bg-[linear-gradient(135deg,rgba(56,189,248,0.10),rgba(2,6,23,0.10))] text-sky-50/82",
      };
    case "audio_primary":
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(16,185,129,0.08),rgba(2,6,23,0.04))]",
        accentClassName: "from-emerald-300/24 via-emerald-300/8 to-transparent",
        chipClassName: "border-emerald-300/20 bg-emerald-300/12 text-emerald-50/88",
        emptyClassName: "border-emerald-300/20 bg-[linear-gradient(135deg,rgba(16,185,129,0.10),rgba(2,6,23,0.10))] text-emerald-50/82",
      };
    case "audio_analysis":
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(34,211,238,0.08),rgba(2,6,23,0.04))]",
        accentClassName: "from-cyan-300/24 via-cyan-300/8 to-transparent",
        chipClassName: "border-cyan-300/20 bg-cyan-300/12 text-cyan-50/88",
        emptyClassName: "border-cyan-300/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(2,6,23,0.10))] text-cyan-50/82",
      };
    case "npc_logic":
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(167,139,250,0.08),rgba(2,6,23,0.04))]",
        accentClassName: "from-violet-300/24 via-violet-300/8 to-transparent",
        chipClassName: "border-violet-300/20 bg-violet-300/12 text-violet-50/88",
        emptyClassName: "border-violet-300/20 bg-[linear-gradient(135deg,rgba(167,139,250,0.10),rgba(2,6,23,0.10))] text-violet-50/82",
      };
    case "projection_haptic":
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(251,191,36,0.08),rgba(2,6,23,0.04))]",
        accentClassName: "from-amber-300/24 via-amber-300/8 to-transparent",
        chipClassName: "border-amber-300/20 bg-amber-300/12 text-amber-50/88",
        emptyClassName: "border-amber-300/20 bg-[linear-gradient(135deg,rgba(251,191,36,0.10),rgba(2,6,23,0.10))] text-amber-50/82",
      };
    case "projection_emotion":
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(168,85,247,0.08),rgba(2,6,23,0.04))]",
        accentClassName: "from-fuchsia-300/24 via-fuchsia-300/8 to-transparent",
        chipClassName: "border-fuchsia-300/20 bg-fuchsia-300/12 text-fuchsia-50/88",
        emptyClassName: "border-fuchsia-300/20 bg-[linear-gradient(135deg,rgba(168,85,247,0.10),rgba(2,6,23,0.10))] text-fuchsia-50/82",
      };
    case "projection_continuity":
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(245,158,11,0.08),rgba(2,6,23,0.04))]",
        accentClassName: "from-amber-300/24 via-amber-300/8 to-transparent",
        chipClassName: "border-amber-300/20 bg-amber-300/12 text-amber-50/88",
        emptyClassName: "border-amber-300/20 bg-[linear-gradient(135deg,rgba(245,158,11,0.10),rgba(2,6,23,0.10))] text-amber-50/82",
      };
    default:
      return {
        shellClassName: "bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(2,6,23,0.04))]",
        accentClassName: "from-white/18 via-white/6 to-transparent",
        chipClassName: "border-white/10 bg-white/[0.06] text-white/74",
        emptyClassName: "border-white/12 bg-[linear-gradient(135deg,rgba(255,255,255,0.06),rgba(2,6,23,0.10))] text-white/78",
      };
  }
}

export function buildWaveHeights(count: number, phase = 0): number[] {
  return Array.from({ length: count }, (_, index) => {
    const wave = Math.sin((index + phase) * 0.82);
    return Math.max(18, Math.round(20 + ((wave + 1) * 18)));
  });
}

export function buildPulseOffsets(count: number): number[] {
  return Array.from({ length: count }, (_, index) => 10 + (index * 12));
}

export function buildSegmentBars(count: number, phase = 0): number[] {
  return Array.from({ length: count }, (_, index) => {
    const wave = Math.sin((index + phase) * 0.74);
    return Math.max(0.16, Math.min(1, 0.28 + ((wave + 1) * 0.34)));
  });
}

export function compressStateChips(labels: string[], lodTier: LaneLodTier) {
  const maxVisible = lodTier === "near" || lodTier === "micro"
    ? labels.length
    : lodTier === "mid"
      ? 2
      : 1;

  return {
    visible: labels.slice(0, maxVisible),
    hiddenCount: Math.max(0, labels.length - maxVisible),
  };
}

export function formatProjectionCouplingSource(raw: string): string {
  if (raw === "track.atomic_runs") return "Atomic Runs";
  if (raw === "runtime.npc") return "NPC Runtime";
  if (raw === "lane.audio.primary") return "Audio Primary";

  return raw
    .replace(/^lane\./, "")
    .split(/[._]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function readDirectCouplingLabel(
  metadata: Record<string, unknown> | null | undefined,
  laneId: string,
  lodTier: LaneLodTier,
): string | null {
  const directCouplings = selectCrossLaneCouplingsForTarget(selectCrossLaneCouplingsFromMetadata(metadata), laneId)
    .filter((coupling) => coupling.kind !== "capsule_membership");
  const primaryCoupling = directCouplings[0] ?? null;

  if (!primaryCoupling) return null;

  return lodTier === "far"
    ? formatProjectionCouplingSource(primaryCoupling.sourceId)
    : `${formatProjectionCouplingSource(primaryCoupling.sourceId)} ${primaryCoupling.state.replace(/_/g, " ")}`;
}

export function readProjectionCouplingLabel(
  metadata: Record<string, unknown> | null | undefined,
  laneId: string,
  lodTier: LaneLodTier,
): string | null {
  return readDirectCouplingLabel(metadata, laneId, lodTier);
}